import { cva } from 'class-variance-authority';

/** List container (<ul>) inside the menu. */
export const menuListClasses =
  'tw:max-h-[min(16rem,60vh)] tw:min-h-0 tw:w-full tw:overflow-y-auto tw:scrollbar-hide tw:p-[var(--spacing-micro)]';

/** Empty state <li> when there are no options. */
export const menuListEmptyStateClasses =
  'tw:px-[var(--spacing-small)] tw:py-[var(--spacing-small)] tw:text-14 tw:text-font';

/** Option button inside the list; styled via variant for selected vs. default. */
export const menuListOptionButtonClasses = cva(
  [
    'tw:flex tw:w-full tw:items-center',
    // 48px touch rows on phones; desktop drops the floor to the compact 8px-padded row.
    'tw:p-[var(--spacing-micro)] tw:min-h-[var(--spacing-colossal)] tw:md:min-h-0',
    'tw:rounded-[var(--radius-8)] tw:text-left tw:text-14 tw:text-font tw:transition-colors',
    'tw:focus:outline-none tw:focus-visible:bg-gray-light tw:focus-visible:ring-2 tw:focus-visible:ring-primary',
  ].join(' '),
  {
    variants: {
      isSelected: {
        // Highlight backgrounds use primary at low opacity composited over the
        // white menu (`bg-primary/20`) rather than the raw `--color-primary-light`
        // token. That token is theme-injected per company (theme-utils.ts), so a
        // company whose theme sets a saturated primary-light rendered the dark
        // `text-font` label invisible against it (PB-4047). An opacity tint over
        // white stays pale in every theme, keeping the label legible. Same
        // opacity pattern as `bg-black/40` below.
        true: 'tw:bg-primary/20 tw:font-medium',
        false: 'tw:hover:bg-primary/20',
      },
      disabled: {
        true: 'tw:text-gray tw:cursor-not-allowed tw:hover:bg-transparent',
        false: '',
      },
    },
    defaultVariants: {
      isSelected: false,
      disabled: false,
    },
  }
);

/** Dimmed backdrop behind the mobile bottom sheet. */
export const menuListMobileBackdropClasses =
  'tw:fixed tw:inset-0 tw:z-40 tw:bg-black/40 tw:transition-opacity animate-backdrop-fade-in';

/** Mobile bottom sheet dialog container. */
export const menuListMobileDialogClasses =
  'tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-50 tw:flex tw:max-h-[85vh] tw:flex-col tw:rounded-t-[var(--radius-16)] tw:border tw:border-solid tw:border-gray tw:border-b-0 tw:bg-white tw:shadow-lg tw:transition-transform animate-bottomsheet-slide-up';

/** Header row in the mobile dialog. */
export const menuListMobileHeaderClasses =
  'tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-[var(--spacing-nano)] tw:border-b tw:border-solid tw:border-gray tw:px-[var(--spacing-base)] tw:py-[var(--spacing-small)]';

/** Title text in the mobile dialog header. */
export const menuListMobileTitleClasses = 'tw:text-16 tw:font-bold tw:text-font';

/** Close button in the mobile dialog header. */
export const menuListMobileCloseButtonClasses =
  'tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:text-gray-dark tw:transition-opacity tw:hover:opacity-80 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary';

/** Search row wrapper in the mobile dialog (shown for filterable selects). */
export const menuListMobileSearchClasses =
  'tw:shrink-0 tw:border-b tw:border-solid tw:border-gray tw:px-[var(--spacing-base)] tw:py-[var(--spacing-small)]';

/** Scrollable body wrapper in the mobile dialog. */
export const menuListMobileBodyClasses =
  'tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:scrollbar-hide tw:pb-[var(--spacing-base)]';

/** Invisible overlay behind the desktop dropdown to establish stacking context. */
export const menuListDesktopOverlayClasses = 'tw:fixed tw:inset-0 tw:z-40 tw:pointer-events-none';

/** Desktop dropdown container. */
// flex column + overflow-hidden so the inline maxHeight actually constrains the list: the <ul>
// carries its own max-h, and without this it would simply overflow the shortened container.
export const menuListDesktopDropdownClasses =
  'tw:z-50 tw:flex tw:flex-col tw:overflow-hidden tw:rounded-[var(--radius-16)] tw:border tw:border-solid tw:border-gray-light tw:bg-white tw:shadow-lg';
